import React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import QRCode from 'qrcode'

export interface QRCodeBoxProps {
  value: string
  size?: number
}

const QRCodeBox: React.FC<QRCodeBoxProps> = ({ value, size = 160 }) => {
  const [dataUrl, setDataUrl] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!value) {
      setDataUrl(null)
      setError(null)
      return
    }

    let cancelled = false

    const generate = async () => {
      try {
        const url = await QRCode.toDataURL(value, {
          width: size,
          margin: 1,
        })

        if (!cancelled) {
          setDataUrl(url)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unable to generate QR code.'
          setError(message)
          setDataUrl(null)
        }
      }
    }

    setDataUrl(null)
    setError(null)
    generate()

    return () => {
      cancelled = true
    }
  }, [value, size])

  if (!value) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          QR code available once the bot username is detected.
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.main',
          color: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  if (!dataUrl) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1.5,
      }}
    >
      <img src={dataUrl} alt="Telegram bot QR" style={{ maxWidth: '100%', maxHeight: '100%' }} />
    </Box>
  )
}

export default QRCodeBox
